import { useEffect, useRef, useState } from "react";

// Palette YOMA (copiée / adaptée)
const C = {
  bg: "#F8F4EE",
  bgCard: "#FFFFFF",
  bgDeep: "#F2EBE0",
  taupe: "#8B7361",
  taupeLight: "#C4B5A5",
  taupeDeep: "#3D2E20",
  beige: "#EDE3D5",
  gold: "#C09A5B",
  goldLight: "#E8D4B0",
  text: "#2E2418",
  muted: "#9A8778",
  border: "#DDD0C0",
  success: "#5C8A5C",
  successLight: "#EAF2EA",
  alert: "#B85C3A",
  alertLight: "#FAF0EB",
  white: "#FFFFFF",
};

const Label = ({ children, required }) => (
  <div
    style={{
      fontFamily: "'DM Sans'",
      fontSize: "10px",
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: C.muted,
      marginBottom: "6px",
    }}
  >
    {children}
    {required && (
      <span style={{ color: C.alert, marginLeft: "3px" }}>*</span>
    )}
  </div>
);

const Input = ({ value, onChange, placeholder, multiline, rows = 3, style: s }) => {
  const base = {
    width: "100%",
    border: `1px solid ${C.border}`,
    borderRadius: "3px",
    padding: "9px 12px",
    background: C.bg,
    color: C.text,
    outline: "none",
    fontSize: "13px",
    fontFamily: "'DM Sans'",
    transition: "border-color .2s",
    ...s,
  };
  const onFocus = (e) => (e.target.style.borderColor = C.gold);
  const onBlur = (e) => (e.target.style.borderColor = C.border);
  if (multiline) {
    return (
      <textarea
        rows={rows}
        style={base}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  }
  return (
    <input
      style={base}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};

const Sel = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={onChange}
    style={{
      width: "100%",
      border: `1px solid ${C.border}`,
      borderRadius: "3px",
      padding: "9px 12px",
      background: C.bg,
      color: C.text,
      fontSize: "13px",
      fontFamily: "'DM Sans'",
      outline: "none",
    }}
  >
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

const Card = ({ children, style }) => (
  <div
    style={{
      background: C.bgCard,
      border: `1px solid ${C.border}`,
      borderRadius: "4px",
      padding: "24px",
      ...style,
    }}
  >
    {children}
  </div>
);

const Grid = ({ cols = 2, children, gap = "16px" }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap,
    }}
  >
    {children}
  </div>
);

const Field = ({ label, children, required }) => (
  <div><Label required={required}>{label}</Label>
    {children}
  </div>
);

const SecTitle = ({ children, sub }) => (
  <div style={{ marginBottom: "16px" }}><div
      style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: "20px",
        color: C.taupeDeep,
        fontWeight: 500,
      }}
    >
      {children}
    </div>
    {sub && (
      <div
        style={{
          fontFamily: "'DM Sans'",
          fontSize: "11px",
          color: C.muted,
          marginTop: "4px",
          letterSpacing: "0.05em",
        }}
      >
        {sub}
      </div>
    )}
  </div>
);

const Btn = ({ children, onClick, variant = "primary", disabled, small, style }) => {
  const vars = {
    primary: { background: C.taupeDeep, color: C.white },
    gold: { background: C.gold, color: C.white },
    ghost: { background: "transparent", color: C.taupe, border: `1px solid ${C.border}` },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      style={{
        fontFamily: "'DM Sans'",
        fontWeight: 600,
        letterSpacing: "0.08em",
        fontSize: small ? "10px" : "11px",
        padding: small ? "7px 14px" : "11px 24px",
        border: "none",
        borderRadius: "3px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        textTransform: "uppercase",
        transition: "all .2s",
        ...vars[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
};

// Config listes (alignées avec Etape1 enrichie)
const TYPE_PROJET = [
  "Appartement",
  "Maison/Villa",
  "Résidence secondaire",
  "Investissement locatif",
  "Riyad",
  "Espace commercial",
  "Autre",
];

const PIECES_INTERIEUR = [
  "Salon principal",
  "Salon TV",
  "Salle à manger",
  "Cuisine principale",
  "Cuisine secondaire/Office",
  "Chambre parentale",
  "Chambre enfants",
  "Chambre invités",
  "Chambre personnel",
  "Salle de bain",
  "WC invités",
  "Bureau",
  "Dressing",
  "Buanderie",
  "Cave",
  "Autre",
];

const PIECES_EXTERIEUR = [
  "Jardin",
  "Terrasse",
  "Patio/Cour intérieure",
  "Rooftop",
  "Piscine",
  "Cuisine extérieure",
  "Salon extérieur",
  "Autre",
];

const OBJECTIFS_OPTIONS = [
  "Transformation esthétique",
  "Amélioration fonctionnalité",
  "Optimisation espace",
  "Modernisation",
  "Préparer revente",
  "Augmenter valeur",
  "Création nouvel espace",
  "Autre",
];

const STYLES_OPTIONS = [
  "Contemporain",
  "Minimaliste",
  "Japandi/Quiet Luxury",
  "Marocain contemporain",
  "Balinais/Méditerranéen",
  "Classique",
  "Mid-Century Modern",
  "Oriental contemporain",
  "Wabi-sabi",
  "Art déco",
  "Bohème luxe",
  "Rustique chic",
  "Autre",
];

const DOC_TYPES = [
  "Plan PDF",
  "Plan DWG",
  "Plan image JPG/PNG",
  "SketchUp SKP",
  "Fichier BAK",
  "Autre",
];

const BUDGET_OPTIONS = [
  "< 200 000 MAD",
  "200 000–500 000 MAD",
  "500 000–1 000 000 MAD",
  "> 1 000 000 MAD",
  "À définir ensemble",
];

const DELAI_OPTIONS = [
  "Urgent (1-3 mois)",
  "Court terme (3-6 mois)",
  "Moyen terme (6-12 mois)",
  "Flexible",
];

const ACCOMPAGNEMENT_OPTIONS = [
  "Conseil ponctuel",
  "Conception aménagement intérieur",
  "Conception + modélisation 3D",
  "Conception + sélection mobilier FF&E",
  "Conception + suivi de chantier",
  "Projet clé en main",
];

const COMMUNICATION_OPTIONS = [
  "Email",
  "WhatsApp",
  "Téléphone",
  "Application YOMA",
  "Autre",
];

function createDefaultForm(record) {
  return {
    clientNom: record?.client_name || "",
    clientEmail: record?.client_email || "",
    typeProjetTypes: [],
    typeProjetAutre: "",
    adresse: "",
    contactNom: record?.client_name || "",
    contactTel: "",
    contactEmail: record?.client_email || "",

    pieces: PIECES_INTERIEUR.map((nom, idx) => ({
      id: idx + 1,
      nom,
      inclure: false,
      nombre: "",
      type: "",
      priorite: "",
    })),
    exterieurs: PIECES_EXTERIEUR.map((nom, idx) => ({
      id: idx + 1,
      nom,
      inclure: false,
      nombre: "",
      type: "",
      priorite: "",
    })),
    piecesSummary: "",

    objectifs: [],
    detailsProjet: "",
    styles: [],

    pinterest: "",
    instagram: "",
    photosInspi: [],

    plansDocs: [],
    plansFiles: [],
    notesPlans: "",
    analysePlans: "",

    occupants: { adultes: "", enfants: "", personnel: "" },
    budget: "",
    delai: "",
    accompagnement: [],
    communication: [],
    contactPrivilegie: "",
  };
}

function extractTokenFromHash() {
  const hash = window.location.hash || "";
  const idx = hash.indexOf("/preconsultation/");
  if (idx === -1) return "";
  const part = hash.slice(idx + "/preconsultation/".length);
  return part.split(/[?#]/)[0];
}

export default function PreConsultationPublic({ token: propToken }) {
  const [token, setToken] = useState("");
  const [record, setRecord] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const documentInputRef = useRef(null);

  useEffect(() => {
    const t = propToken || extractTokenFromHash();
    if (!t) {
      setError("Lien invalide ou incomplet.");
      setLoading(false);
      return;
    }
    setToken(t);

    (async () => {
      try {
        const { supabase } = await import("../supabaseClient");
        const { data, error: err } = await supabase
          .from("pre_consultations")
          .select("*")
          .eq("token", t)
          .single();

        if (err || !data) {
          setError("Ce lien n'est pas valide ou a expiré.");
        } else if (data.status === "questionnaire_recu") {
          setRecord(data);
          setSubmitted(true);
        } else {
          setRecord(data);
          setFormData(data.data || createDefaultForm(data));
          try {
            const { supabase: sb } = await import("../supabaseClient");
            const res = await sb.functions.invoke("list-preconsultation-documents", {
              body: { token: t },
            });
            if (res.data && Array.isArray(res.data)) {
              setUploadedDocuments(res.data);
            }
          } catch (_) {
            setUploadedDocuments([]);
          }
        }
      } catch (e) {
        setError("Erreur lors du chargement du questionnaire.");
      } finally {
        setLoading(false);
      }
    })();
  }, [propToken]);

  const updateForm = (updater) => {
    setFormData((prev) =>
      typeof updater === "function" ? updater(prev || {}) : updater
    );
  };

  const toggleArrayField = (field, value) => {
    updateForm((d) => {
      const prev = d[field] || [];
      const exists = prev.includes(value);
      return {
        ...d,
        [field]: exists ? prev.filter((v) => v !== value) : [...prev, value],
      };
    });
  };

  const updatePieces = (index, field, value) => {
    updateForm((d) => {
      const list = (d.pieces || createDefaultForm(record).pieces).map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      );
      return { ...d, pieces: list };
    });
  };

  const updateExterieurs = (index, field, value) => {
    updateForm((d) => {
      const list = (d.exterieurs || createDefaultForm(record).exterieurs).map(
        (p, i) => (i === index ? { ...p, [field]: value } : p)
      );
      return { ...d, exterieurs: list };
    });
  };

  const handlePhotosInspiUpload = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const readers = files.map(
      (f) =>
        new Promise((res) => {
          const r = new FileReader();
          r.onload = () => {
            const result = r.result || "";
            const str = typeof result === "string" ? result : "";
            const base64 = str.includes(",") ? str.split(",")[1] : str;
            res({
              id: Date.now() + Math.random(),
              name: f.name,
              url: str,
              type: f.type,
              data: base64,
              comment: "",
            });
          };
          r.readAsDataURL(f);
        })
    );
    Promise.all(readers).then((newImgs) => {
      updateForm((d) => {
        const current = d.photosInspi || [];
        const merged = [...current, ...newImgs].slice(0, 4);
        return { ...d, photosInspi: merged };
      });
    });
  };

  function formatBytes(bytes) {
    if (bytes == null || bytes === 0) return "—";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }

  const MAX_FILE_SIZE_BASE64 = 2 * 1024 * 1024; // 2 Mo — MVP temporaire ; prochaine étape : Signed Upload URL (voir docs/UPLOAD_DWG_PDF_Strategie.md)

  async function handleDocumentFilesChange(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadError("");
    const tooBig = files.find((f) => f.size > MAX_FILE_SIZE_BASE64);
    if (tooBig) {
      setUploadError(`« ${tooBig.name } » dépasse 2 Mo. Réduisez la taille du fichier ou contactez-nous pour un envoi direct.`);
      if (documentInputRef.current) documentInputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const { supabase: sb } = await import("../supabaseClient");
      for (const file of files) {
        const base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => {
            const str = r.result || "";
            const part = typeof str === "string" && str.includes(",") ? str.split(",")[1] : "";
            resolve(part);
          };
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        const res = await sb.functions.invoke("upload-preconsultation-document", {
          body: { token, file: base64, name: file.name, type: file.type },
        });
        if (res.error) {
          setUploadError(res.error.message || "Échec de l'envoi d'un fichier.");
          break;
        }
        if (res.data && typeof res.data === "object") {
          setUploadedDocuments((prev) => [...prev, res.data]);
        }
      }
    } catch (err) {
      setUploadError(err?.message || "Erreur lors de l'envoi.");
    } finally {
      setUploading(false);
      if (documentInputRef.current) documentInputRef.current.value = "";
    }
  }

  async function submitQuestionnaire() {
    try {
      const { supabase } = await import("../supabaseClient");

      // 1. Sauvegarder les données
      await supabase
        .from("pre_consultations")
        .update({
          data: formData,
          status: "questionnaire_recu",
          submitted_at: new Date().toISOString(),
        })
        .eq("token", token);

      // 2. Email de confirmation au client
      await supabase.functions.invoke("send-email", {
        body: {
          to: record.client_email,
          subject: "YOMA DESIGN — Votre dossier a bien été reçu",
          html: `
        <div style="font-family: Georgia, serif; max-width: 600px; 
          margin: 0 auto; padding: 40px; background: #FAF7F2;"><div style="text-align: center; margin-bottom: 32px;"><h1 style="font-size: 28px; color: #3D2E20; 
              letter-spacing: 0.15em; font-weight: 300;">
              YOMA DESIGN
            </h1></div><p style="color: #3D2E20; font-size: 16px; line-height: 1.8;">
            Cher(e) <strong>${record.client_name}</strong>,
          </p><p style="color: #5C4A35; font-size: 15px; line-height: 1.9; 
            margin-top: 16px;">
            Nous avons bien reçu votre questionnaire de pré-consultation. 
            Merci pour le temps que vous y avez consacré.
          </p><p style="color: #5C4A35; font-size: 15px; line-height: 1.9;">
            Notre équipe va étudier votre projet avec la plus grande attention 
            et vous contactera très prochainement pour convenir d'une 
            première rencontre.
          </p><div style="background: #EDE3D5; padding: 24px; 
            border-radius: 3px; margin: 32px 0; 
            border-left: 4px solid #C09A5B;"><p style="color: #3D2E20; font-size: 14px; 
              font-style: italic; line-height: 1.8; margin: 0;">
              "Chaque projet est unique. Nous mettons tout notre savoir-faire 
              à votre service pour créer un espace qui vous ressemble."
            </p><p style="color: #9A8778; font-size: 12px; 
              margin-top: 8px; margin-bottom: 0;">
              — YOMA DESIGN
            </p></div><div style="border-top: 1px solid #DDD0C0; margin-top: 40px; 
            padding-top: 24px; text-align: center;"><p style="color: #9A8778; font-size: 12px; line-height: 1.8;">
              YOMA DESIGN SARL AU — Marrakech, Maroc<br/>
              contact@yoma.design
            </p></div></div>
      `,
        },
      });

      // 2b. Email résumé vers contact@yoma.design
      const d = formData || {};
      const summaryHtml = `
        <div style="font-family: sans-serif; max-width: 600px; padding: 24px;"><h2 style="color: #3D2E20;">Nouveau questionnaire pré-consultation</h2><table style="width: 100%; border-collapse: collapse; font-size: 13px;"><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Nom</td><td>${d.contactNom ?? d.clientNom ?? "—"}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Email</td><td>${d.contactEmail ?? d.clientEmail ?? record?.client_email ?? "—"}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Téléphone</td><td>${d.contactTel ?? "—"}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Adresse du projet</td><td>${d.adresse ?? "—"}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Budget</td><td>${d.budget ?? "—"}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Délai</td><td>${d.delai ?? "—"}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Type de projet</td><td>${[].concat(d.typeProjetTypes || [], d.typeProjetAutre ? [d.typeProjetAutre] : []).filter(Boolean).join(", ") || "—"}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Accompagnement souhaité</td><td>${Array.isArray(d.accompagnement) ? d.accompagnement.join(", ") : (d.accompagnement ?? "—")}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Objectifs</td><td>${Array.isArray(d.objectifs) ? d.objectifs.join(", ") : (d.objectifs ?? "—")}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Styles</td><td>${Array.isArray(d.styles) ? d.styles.join(", ") : (d.styles ?? "—")}</td></tr><tr><td style="padding: 6px 0; font-weight: 600; color: #5C4A35;">Détails du projet</td><td>${d.detailsProjet ?? "—"}</td></tr></table></div>
      `;
      await supabase.functions.invoke("send-email", {
        body: {
          to: "contact@yoma.design",
          subject: `[Pré-consultation] ${d.contactNom ?? d.clientNom ?? "Nouveau dossier"} — Résumé`,
          html: summaryHtml,
        },
      });

      // 3. Afficher page de confirmation
      setSubmitted(true);
    } catch (e) {
      alert("Erreur lors de l'envoi du dossier.");
    }
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: C.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui",
          fontSize: 13,
          color: C.muted,
        }}
      >
        Chargement du questionnaire…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: C.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          boxSizing: "border-box",
        }}
      ><Card style={{ maxWidth: 520 }}><div
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 20,
              color: C.alert,
              marginBottom: 8,
            }}
          >
            Lien indisponible
          </div><div
            style={{
              fontFamily: "'DM Sans'",
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.7,
            }}
          >
            {error}
          </div></Card></div>
    );
  }

  if (submitted) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: C.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          boxSizing: "border-box",
        }}
      ><Card style={{ maxWidth: 560, textAlign: "center" }}><div
            style={{
              fontFamily: "'Playfair Display'",
              fontSize: 40,
              color: C.gold,
              marginBottom: 12,
            }}
          >
            ◆
          </div><div
            style={{
              fontFamily: "'Playfair Display'",
              fontSize: 22,
              color: C.taupeDeep,
              marginBottom: 8,
            }}
          >
            Dossier envoyé avec succès
          </div><div
            style={{
              fontFamily: "'DM Sans'",
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.8,
              marginBottom: 16,
            }}
          >
            Merci {record?.client_name}. Nous avons bien reçu votre
            questionnaire et vous en avons envoyé une confirmation par email.
            Notre équipe reviendra vers vous très prochainement.
          </div><div
            style={{
              fontFamily: "'DM Sans'",
              fontSize: 12,
              color: C.gold,
              marginBottom: 6,
            }}
          >
            contact@yoma.design
          </div><div
            style={{
              fontFamily: "'Playfair Display'",
              fontSize: 14,
              color: C.taupeDeep,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              marginTop: 12,
            }}
          >
            YOMA DESIGN
          </div></Card></div>
    );
  }

  if (!formData) return null;

  const occupants = formData.occupants || {};

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        justifyContent: "center",
        padding: "32px 16px",
        boxSizing: "border-box",
      }}
    ><div style={{ width: "100%", maxWidth: 900 }}>
        {/* Header YOMA DESIGN */}
        <div
          style={{
            background: C.taupeDeep,
            borderRadius: "8px",
            padding: "20px 24px",
            marginBottom: 24,
            color: C.white,
          }}
        ><div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              borderBottom: "1px solid rgba(255,255,255,0.16)",
              paddingBottom: 12,
              marginBottom: 10,
            }}
          ><div><div
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 20,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                }}
              >
                YOMA DESIGN
              </div><div
                style={{
                  fontFamily: "'DM Sans'",
                  fontSize: 10,
                  color: C.taupeLight,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  marginTop: 4,
                }}
              >
                Architecture Intérieure — Marrakech
              </div></div></div><div><div
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 18,
                color: C.white,
                marginBottom: 4,
              }}
            >
              Bonjour {record?.client_name || formData.clientNom || "cher client"},
            </div><div
              style={{
                fontFamily: "'DM Sans'",
                fontSize: 13,
                color: C.beige,
                lineHeight: 1.7,
              }}
            >
              Bienvenue dans votre espace de pré-consultation YOMA DESIGN. Ce
              questionnaire nous aidera à comprendre votre projet et vos
              attentes avant notre première rencontre.
            </div></div></div><div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* SECTION 1 — Type & Identification du projet */}
          <Card><SecTitle sub="Contexte général du projet et interlocuteur principal">
              Type & Identification du projet
            </SecTitle><div style={{ display: "flex", flexDirection: "column", gap: 14 }}><div><Label>Type de projet</Label><div
                  style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                >
                  {TYPE_PROJET.map((t) => {
                    const checked = (formData.typeProjetTypes || []).includes(t);
                    return (
                      <label
                        key={t}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: "11px",
                          color: C.text,
                          background: C.bgCard,
                          borderRadius: "3px",
                          padding: "5px 8px",
                          border: `1px solid ${C.border}`,
                        }}
                      ><input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            updateForm((d) => {
                              const prev = d.typeProjetTypes || [];
                              const exists = prev.includes(t);
                              const next = exists
                                ? prev.filter((v) => v !== t)
                                : [...prev, t];
                              return {
                                ...d,
                                typeProjetTypes: next,
                              };
                            })
                          }
                        /><span>{t}</span></label>
                    );
                  })}
                </div></div><Field label="Adresse complète du projet"><Input
                  value={formData.adresse || ""}
                  onChange={(e) =>
                    updateForm((d) => ({ ...d, adresse: e.target.value }))
                  }
                  placeholder="Adresse complète, résidence, ville…"
                /></Field><Grid cols={3}><Field label="Contact principal — Nom"><Input
                    value={formData.contactNom || ""}
                    onChange={(e) =>
                      updateForm((d) => ({
                        ...d,
                        contactNom: e.target.value,
                      }))
                    }
                    placeholder="Nom / Famille"
                  /></Field><Field label="Téléphone"><Input
                    value={formData.contactTel || ""}
                    onChange={(e) =>
                      updateForm((d) => ({
                        ...d,
                        contactTel: e.target.value,
                      }))
                    }
                    placeholder="+212…"
                  /></Field><Field label="Email"><Input
                    value={formData.contactEmail || ""}
                    onChange={(e) =>
                      updateForm((d) => ({
                        ...d,
                        contactEmail: e.target.value,
                      }))
                    }
                    placeholder="email@exemple.com"
                  /></Field></Grid></div></Card>

          {/* SECTION 2 — Espaces intérieurs */}
          <Card><SecTitle sub="Pièces intérieures concernées par la mission">
              Espaces intérieurs
            </SecTitle><div style={{ overflowX: "auto" }}><table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                }}
              ><thead><tr style={{ background: C.beige }}>
                    {["Pièce", "Inclure", "Nombre", "Type / Précision", "Priorité"].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            padding: "8px 10px",
                            textAlign: "left",
                            fontSize: "9px",
                            fontWeight: 600,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: C.muted,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr></thead><tbody>
                  {(formData.pieces || []).map((p, idx) => (
                    <tr
                      key={p.id}
                      style={{
                        background: idx % 2 === 0 ? C.bgCard : C.bg,
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    ><td style={{ padding: "6px 8px" }}>{p.nom}</td><td style={{ padding: "6px 8px" }}><input
                          type="checkbox"
                          checked={!!p.inclure}
                          onChange={(e) =>
                            updatePieces(idx, "inclure", e.target.checked)
                          }
                        /></td><td style={{ padding: "6px 8px" }}><input
                          type="number"
                          min="1"
                          value={p.nombre || ""}
                          onChange={(e) =>
                            updatePieces(idx, "nombre", e.target.value)
                          }
                          style={{
                            width: "60px",
                            fontSize: "11px",
                            padding: "3px 6px",
                            borderRadius: "3px",
                            border: `1px solid ${C.border}`,
                          }}
                        /></td><td style={{ padding: "6px 8px" }}><input
                          type="text"
                          value={p.type || ""}
                          onChange={(e) =>
                            updatePieces(idx, "type", e.target.value)
                          }
                          placeholder="Suite parentale, enfants…"
                          style={{
                            width: "160px",
                            fontSize: "11px",
                            padding: "3px 6px",
                            borderRadius: "3px",
                            border: `1px solid ${C.border}`,
                          }}
                        /></td><td style={{ padding: "6px 8px" }}><input
                          type="number"
                          min="1"
                          value={p.priorite || ""}
                          onChange={(e) =>
                            updatePieces(idx, "priorite", e.target.value)
                          }
                          style={{
                            width: "70px",
                            fontSize: "11px",
                            padding: "3px 6px",
                            borderRadius: "3px",
                            border: `1px solid ${C.border}`,
                          }}
                        /></td></tr>
                  ))}
                </tbody></table></div></Card>

          {/* SECTION 3 — Espaces extérieurs */}
          <Card><SecTitle sub="Espaces extérieurs et prolongements du projet">
              Espaces extérieurs
            </SecTitle><div style={{ overflowX: "auto" }}><table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                }}
              ><thead><tr style={{ background: C.beige }}>
                    {[
                      "Espace extérieur",
                      "Inclure",
                      "Nombre",
                      "Type / Précision",
                      "Priorité",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "8px 10px",
                          textAlign: "left",
                          fontSize: "9px",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: C.muted,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr></thead><tbody>
                  {(formData.exterieurs || []).map((p, idx) => (
                    <tr
                      key={p.id}
                      style={{
                        background: idx % 2 === 0 ? C.bgCard : C.bg,
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    ><td style={{ padding: "6px 8px" }}>{p.nom}</td><td style={{ padding: "6px 8px" }}><input
                          type="checkbox"
                          checked={!!p.inclure}
                          onChange={(e) =>
                            updateExterieurs(idx, "inclure", e.target.checked)
                          }
                        /></td><td style={{ padding: "6px 8px" }}><input
                          type="number"
                          min="1"
                          value={p.nombre || ""}
                          onChange={(e) =>
                            updateExterieurs(idx, "nombre", e.target.value)
                          }
                          style={{
                            width: "60px",
                            fontSize: "11px",
                            padding: "3px 6px",
                            borderRadius: "3px",
                            border: `1px solid ${C.border}`,
                          }}
                        /></td><td style={{ padding: "6px 8px" }}><input
                          type="text"
                          value={p.type || ""}
                          onChange={(e) =>
                            updateExterieurs(idx, "type", e.target.value)
                          }
                          placeholder="Terrasse couverte, rooftop lounge…"
                          style={{
                            width: "160px",
                            fontSize: "11px",
                            padding: "3px 6px",
                            borderRadius: "3px",
                            border: `1px solid ${C.border}`,
                          }}
                        /></td><td style={{ padding: "6px 8px" }}><input
                          type="number"
                          min="1"
                          value={p.priorite || ""}
                          onChange={(e) =>
                            updateExterieurs(idx, "priorite", e.target.value)
                          }
                          style={{
                            width: "70px",
                            fontSize: "11px",
                            padding: "3px 6px",
                            borderRadius: "3px",
                            border: `1px solid ${C.border}`,
                          }}
                        /></td></tr>
                  ))}
                </tbody></table></div></Card>

          {/* SECTION 4 — Objectifs du projet */}
          <Card><SecTitle sub="Pourquoi ce projet, quels sont vos objectifs principaux ?">
              Objectifs du projet
            </SecTitle><div style={{ display: "flex", flexDirection: "column", gap: 12 }}><div><Label>Objectifs principaux</Label><div
                  style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                >
                  {OBJECTIFS_OPTIONS.map((o) => {
                    const checked = (formData.objectifs || []).includes(o);
                    return (
                      <label
                        key={o}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: "11px",
                          color: C.text,
                          background: C.bgCard,
                          borderRadius: "3px",
                          padding: "5px 8px",
                          border: `1px solid ${C.border}`,
                        }}
                      ><input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleArrayField("objectifs", o)}
                        /><span>{o}</span></label>
                    );
                  })}
                </div></div><Field label="Détails sur le projet"><Input
                  multiline
                  rows={3}
                  value={formData.detailsProjet || ""}
                  onChange={(e) =>
                    updateForm((d) => ({
                      ...d,
                      detailsProjet: e.target.value,
                    }))
                  }
                  placeholder="Contexte, histoire du lieu, attentes spécifiques, contraintes personnelles…"
                /></Field></div></Card>

          {/* SECTION 5 — Style & Inspirations */}
          <Card><SecTitle sub="Univers esthétiques et atmosphères vers lesquels tendre">
              Style & Inspirations
            </SecTitle><div style={{ display: "flex", flexDirection: "column", gap: 10 }}><Label>Styles qui vous parlent</Label><div
                style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
              >
                {STYLES_OPTIONS.map((s) => {
                  const checked = (formData.styles || []).includes(s);
                  return (
                    <label
                      key={s}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: "11px",
                        color: C.text,
                        background: C.bgCard,
                        borderRadius: "3px",
                        padding: "5px 8px",
                        border: `1px solid ${C.border}`,
                      }}
                    ><input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          updateForm((d) => {
                            const prev = d.styles || [];
                            const exists = prev.includes(s);
                            const next = exists
                              ? prev.filter((v) => v !== s)
                              : [...prev, s];
                            return { ...d, styles: next };
                          })
                        }
                      /><span>{s}</span></label>
                  );
                })}
              </div></div></Card>

          {/* SECTION 6 — Inspirations visuelles */}
          <Card><SecTitle sub="Références en ligne et photos d'ambiance">
              Inspirations visuelles
            </SecTitle><div style={{ display: "flex", flexDirection: "column", gap: 12 }}><Grid cols={2}><Field label="Lien Pinterest"><Input
                    value={formData.pinterest || ""}
                    onChange={(e) =>
                      updateForm((d) => ({
                        ...d,
                        pinterest: e.target.value,
                      }))
                    }
                    placeholder="https://www.pinterest.com/…"
                  /></Field><Field label="Lien Instagram"><Input
                    value={formData.instagram || ""}
                    onChange={(e) =>
                      updateForm((d) => ({
                        ...d,
                        instagram: e.target.value,
                      }))
                    }
                    placeholder="https://www.instagram.com/…"
                  /></Field></Grid><div><Label>Photos d'inspiration (max. 4)</Label><input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    handlePhotosInspiUpload(e.target.files);
                    e.target.value = "";
                  }}
                  style={{ fontSize: "11px" }}
                />
                {(formData.photosInspi || []).length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    {(formData.photosInspi || []).map((p) => (
                      <div
                        key={p.id}
                        style={{
                          border: `1px solid ${C.border}`,
                          borderRadius: "3px",
                          overflow: "hidden",
                          background: C.bg,
                          maxWidth: 140,
                        }}
                      ><img
                          src={p.url}
                          alt={p.name}
                          style={{
                            width: 140,
                            height: 90,
                            objectFit: "cover",
                            display: "block",
                          }}
                        /><input
                          type="text"
                          value={p.comment || ""}
                          onChange={(e) =>
                            updateForm((d) => {
                              const list = (d.photosInspi || []).map((img) =>
                                img.id === p.id
                                  ? { ...img, comment: e.target.value }
                                  : img
                              );
                              return { ...d, photosInspi: list };
                            })
                          }
                          placeholder="Ce que j'aime dans cette photo"
                          style={{
                            width: "100%",
                            fontSize: "9px",
                            padding: "4px 6px",
                            border: "none",
                            borderTop: `1px solid ${C.border}`,
                            outline: "none",
                          }}
                        /></div>
                    ))}
                  </div>
                )}
              </div></div></Card>

          {/* SECTION 7 — Documents techniques */}
          <Card><SecTitle sub="Plans et documents existants pour analyse">
              Documents techniques
            </SecTitle><div style={{ display: "flex", flexDirection: "column", gap: 12 }}><div><Label>Types de documents disponibles</Label><div
                  style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                >
                  {DOC_TYPES.map((t) => {
                    const checked = (formData.plansDocs || []).includes(t);
                    return (
                      <label
                        key={t}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: "11px",
                          color: C.text,
                          background: C.bgCard,
                          borderRadius: "3px",
                          padding: "5px 8px",
                          border: `1px solid ${C.border}`,
                        }}
                      ><input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            updateForm((d) => {
                              const prev = d.plansDocs || [];
                              const exists = prev.includes(t);
                              const next = exists
                                ? prev.filter((v) => v !== t)
                                : [...prev, t];
                              return { ...d, plansDocs: next };
                            })
                          }
                        /><span>{t}</span></label>
                    );
                  })}
                </div></div><div><Label>Fichiers (plans, documents, images)</Label><input
                  ref={documentInputRef}
                  type="file"
                  accept=".pdf,.dwg,image/*"
                  multiple
                  disabled={uploading}
                  onChange={handleDocumentFilesChange}
                  style={{ display: "none" }}
                /><div
                  role="button"
                  tabIndex={0}
                  onClick={() => documentInputRef.current?.click()}
                  onKeyDown={(e) => e.key === "Enter" && documentInputRef.current?.click()}
                  style={{
                    border: `2px dashed ${C.border}`,
                    borderRadius: "3px",
                    padding: "20px",
                    textAlign: "center",
                    cursor: uploading ? "wait" : "pointer",
                    background: C.bg,
                    transition: "border-color .2s",
                    opacity: uploading ? 0.8 : 1,
                  }}
                  onMouseEnter={(e) => !uploading && (e.currentTarget.style.borderColor = C.gold)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}
                ><div style={{ fontFamily: "'Playfair Display'", fontSize: "22px", color: C.taupeLight }}>◎</div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted, marginTop: "6px" }}>
                    {uploading ? "Envoi en cours…" : "Glisser ou cliquer — PDF, DWG, images"}
                  </div></div>
                {uploadError && (
                  <div style={{ marginTop: 8, fontSize: "12px", color: C.alert }}>{uploadError}</div>
                )}
                {uploadedDocuments.length > 0 && (
                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                    {uploadedDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          background: C.bgCard,
                          border: `1px solid ${C.border}`,
                          borderRadius: "3px",
                          fontSize: "12px",
                        }}
                      ><div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}><span style={{ color: C.muted, flexShrink: 0 }}></span><span style={{ color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.name}>
                            {doc.name}
                          </span><span style={{ color: C.muted, flexShrink: 0 }}>({doc.extension || doc.category})</span><span style={{ color: C.muted, flexShrink: 0 }}>{formatBytes(doc.size)}</span></div>
                        {doc.signed_url && (
                          <a
                            href={doc.signed_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: C.gold, textDecoration: "none", fontSize: "11px", flexShrink: 0 }}
                          >
                            Télécharger
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div><Field label="Notes sur les plans fournis"><Input
                  multiline
                  rows={3}
                  value={formData.notesPlans || ""}
                  onChange={(e) =>
                    updateForm((d) => ({
                      ...d,
                      notesPlans: e.target.value,
                    }))
                  }
                  placeholder="Précisez l'état des plans, les zones modifiées, les incohérences éventuelles…"
                /></Field></div></Card>

          {/* SECTION 8 — Occupation du logement */}
          <Card><SecTitle sub="Composition du foyer et usage des espaces">
              Occupation du logement
            </SecTitle><div style={{ maxWidth: 420 }}><table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                }}
              ><tbody>
                  {["Adultes", "Enfants", "Personnel"].map((row) => {
                    const key = row.toLowerCase();
                    return (
                      <tr key={row}><td
                          style={{
                            padding: "6px 8px",
                            fontSize: "11px",
                            color: C.text,
                          }}
                        >
                          {row}
                        </td><td
                          style={{ padding: "6px 8px", textAlign: "right" }}
                        ><input
                            type="number"
                            min="0"
                            value={occupants[key] || ""}
                            onChange={(e) =>
                              updateForm((d) => ({
                                ...d,
                                occupants: {
                                  ...(d.occupants || {}),
                                  [key]: e.target.value,
                                },
                              }))
                            }
                            style={{
                              width: "80px",
                              fontSize: "11px",
                              padding: "3px 6px",
                              borderRadius: "3px",
                              border: `1px solid ${C.border}`,
                            }}
                          /></td></tr>
                    );
                  })}
                </tbody></table></div></Card>

          {/* SECTION 9 — Budget */}
          <Card><SecTitle sub="Enveloppe budgétaire prévisionnelle pour le projet">
              Budget
            </SecTitle><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {BUDGET_OPTIONS.map((b) => (
                <label
                  key={b}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: "11px",
                    color: C.text,
                    background: C.bgCard,
                    borderRadius: "3px",
                    padding: "5px 8px",
                    border: `1px solid ${C.border}`,
                  }}
                ><input
                    type="radio"
                    name="budget-range"
                    checked={formData.budget === b}
                    onChange={() =>
                      updateForm((d) => ({ ...d, budget: b }))
                    }
                  /><span>{b}</span></label>
              ))}
            </div></Card>

          {/* SECTION 10 — Délais souhaités */}
          <Card><SecTitle sub="Horizon de temps souhaité pour le projet">
              Délais souhaités
            </SecTitle><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {DELAI_OPTIONS.map((dOpt) => (
                <label
                  key={dOpt}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: "11px",
                    color: C.text,
                    background: C.bgCard,
                    borderRadius: "3px",
                    padding: "5px 8px",
                    border: `1px solid ${C.border}`,
                  }}
                ><input
                    type="radio"
                    name="delai-range"
                    checked={formData.delai === dOpt}
                    onChange={() =>
                      updateForm((d) => ({ ...d, delai: dOpt }))
                    }
                  /><span>{dOpt}</span></label>
              ))}
            </div></Card>

          {/* SECTION 11 — Niveau d'accompagnement */}
          <Card><SecTitle sub="De quel niveau de service avez-vous besoin ?">
              Niveau d'accompagnement
            </SecTitle><div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ACCOMPAGNEMENT_OPTIONS.map((o) => {
                const checked = (formData.accompagnement || []).includes(o);
                return (
                  <label
                    key={o}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: "11px",
                      color: C.text,
                    }}
                  ><input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleArrayField("accompagnement", o)}
                    /><span>{o}</span></label>
                );
              })}
            </div></Card>

          {/* SECTION 12 — Communication */}
          <Card><SecTitle sub="Canaux privilégiés pour le suivi et les échanges">
              Communication
            </SecTitle><div style={{ display: "flex", flexDirection: "column", gap: 10 }}><div
                style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
              >
                {COMMUNICATION_OPTIONS.map((cOpt) => {
                  const checked = (formData.communication || []).includes(
                    cOpt
                  );
                  return (
                    <label
                      key={cOpt}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: "11px",
                        color: C.text,
                        background: C.bgCard,
                        borderRadius: "3px",
                        padding: "5px 8px",
                        border: `1px solid ${C.border}`,
                      }}
                    ><input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          toggleArrayField("communication", cOpt)
                        }
                      /><span>{cOpt}</span></label>
                  );
                })}
              </div><Field label="Contact privilégié pour le projet"><Input
                  value={formData.contactPrivilegie || ""}
                  onChange={(e) =>
                    updateForm((d) => ({
                      ...d,
                      contactPrivilegie: e.target.value,
                    }))
                  }
                  placeholder="Nom, rôle et coordonnées de la personne de référence…"
                /></Field></div></Card>

          {/* SECTION 13 — Message de fin */}
          <Card
            style={{
              background: C.beige,
              border: `1px solid ${C.goldLight}`,
            }}
          ><div style={{ textAlign: "center" }}><div
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "24px",
                  color: C.taupeDeep,
                  marginBottom: "8px",
                }}
              >
                Merci pour votre confiance
              </div><div
                style={{
                  fontFamily: "'DM Sans'",
                  fontSize: "12px",
                  color: C.muted,
                  maxWidth: "520px",
                  margin: "0 auto",
                  lineHeight: 1.7,
                }}
              >
                Merci d'avoir pris le temps de remplir ce questionnaire. Nous
                l'utiliserons pour préparer notre rencontre et vous proposer un
                accompagnement sur mesure, fidèle à vos besoins et à votre
                style.
              </div><div
                style={{
                  fontFamily: "'DM Sans'",
                  fontSize: "12px",
                  color: C.gold,
                  marginTop: "10px",
                }}
              >
                contact@yoma.design
              </div><div
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "16px",
                  color: C.taupeDeep,
                  marginTop: "4px",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                YOMA DESIGN
              </div></div></Card>

          {/* Bouton final d'envoi */}
          <div style={{ textAlign: "center", marginTop: 8, marginBottom: 32 }}><Btn
              variant="gold"
              onClick={submitQuestionnaire}
              style={{ padding: "14px 36px", fontSize: "12px" }}
            >
              Envoyer mon dossier à YOMA DESIGN
            </Btn></div></div></div></div>
  );
}

